/* lib/diag/navLoc.js — EL DISCRIMINADOR H1 vs H2, y es UNA sola pregunta.
 *
 * ── QUÉ DECIDE ──────────────────────────────────────────────────────────────
 * El recon del congelamiento (2026-08-16) dejó dos hipótesis principales para
 * "vibró y la pestaña no cambió", y desde el registro del visor eran
 * INDISTINGUIBLES. La pregunta que las separa es una sola:
 *
 *   ¿`window.location.pathname` se movió mientras la pantalla seguía pintando
 *   la pestaña vieja?
 *
 *   · SÍ  → H2. El click SÍ llegó al `<Link>`, `pushState` corrió, y quien no
 *           commiteó fue React: `BrowserRouter` de react-router 7 envuelve toda
 *           actualización de ubicación en `React.startTransition`, y esa
 *           actualización es interrumpible y diferible. La URL ya se movió; el
 *           riel del carril, que sale de `useLocation()`, no.
 *   · NO  → H1. El click nunca llegó al `<Link>`: se lo tragó una de las
 *           compuertas del embudo de la píldora (ver `NAV.TAP`).
 *
 * ── POR QUÉ DOS MUESTRAS Y NO UNA ───────────────────────────────────────────
 * La primera (`fase:'commit'`) se toma en el instante en que el click ya subió
 * por el `<Link>`: ahí `location` YA cambió si el click llegó, pero React casi
 * nunca ha commiteado todavía (la transición es diferida por diseño), así que
 * una discrepancia en esta muestra es NORMAL y no prueba nada.
 *
 * La segunda (`fase:'eco'`, ~1 s después) es la que juzga: a ese plazo una
 * transición sana ya commiteó hace mucho. Si en el eco `loc` y `react` siguen
 * discrepando, la transición NO llegó — H2 en flagrante. Si en el eco los dos
 * dicen lo mismo y ese "mismo" es la pestaña VIEJA, el click nunca navegó — H1.
 *
 * Por eso `rutaReact` se acepta como FUNCIÓN: el eco tiene que leer lo que React
 * está pintando EN ESE MOMENTO, no lo que pintaba al soltar el dedo. Pasar un
 * valor plano contestaría la pregunta equivocada.
 *
 * ── CONTRATOS (los mismos del visor entero) ─────────────────────────────────
 *   · APP-ONLY: en web cada llamada es un return temprano. Cero timers, cero
 *     lecturas, comportamiento byte-idéntico.
 *   · JAMÁS DECIDE NADA: nadie lee lo que aquí se escribe. Todo va en try/catch;
 *     un fallo del instrumento no puede tocar una navegación.
 *   · EL ÚNICO TIMER es el del eco, uno por navegación, y muere solo.
 */
import { IS_APP_BUILD } from '../buildTarget';
import { log as diagLog } from './readyChainLog';

/** Cuánto se espera para la segunda muestra. ~1 s es lo que pidió el recon: muy
 *  por encima de lo que tarda una transición sana en commitear (milisegundos) y
 *  muy por debajo de lo que un usuario tolera antes de volver a tocar. */
export const NAV_LOC_ECO_MS = 1000;

function pathnameDeVentana() {
  try {
    return (window && window.location && window.location.pathname) || '';
  } catch (e) {
    return '';
  }
}

function leer(rutaReact) {
  try {
    return typeof rutaReact === 'function' ? rutaReact() : rutaReact;
  } catch (e) {
    return null;
  }
}

/**
 * logNavLoc(origen, rutaReact) — apunta las dos muestras de la navegación.
 *
 * @param {string} origen  quién disparó el click ('pildora' — el único embudo
 *                         de navegación de tabs que existe hoy).
 * @param {function|string} rutaReact  el `pathname` que React está pintando.
 *                         FUNCIÓN de preferencia: el eco la vuelve a llamar.
 * @param {object} [extra]  campos sueltos que el llamador quiera adjuntar
 *                         (p. ej. si el click era propio del gesto).
 */
export function logNavLoc(origen, rutaReact, extra) {
  if (!IS_APP_BUILD) return;
  const escribir = (fase) => {
    try {
      diagLog('NAV.LOC', {
        origen,
        fase,
        loc: pathnameDeVentana(),
        react: leer(rutaReact) ?? null,
        ...(extra || {}),
      });
    } catch (e) { /* el visor jamás decide una navegación */ }
  };
  escribir('commit');
  try {
    setTimeout(() => escribir('eco'), NAV_LOC_ECO_MS);
  } catch (e) { /* sin eco quedan las líneas de commit, que ya dicen la mitad */ }
}

export default logNavLoc;
